import { MainText } from '@/components/Typography';
import useProductStore from '@/features/Product/store/ProductStore';
import type { ProductT } from '@/features/Product/types';
import { PRODUCT_COLUMNS } from '@/types/filterKeyConst';
import { filterMap } from '@/utils/lazy';

export const useProductFilterMap = () => {
  const { productMap, brandMap, lineMap } = useProductStore((state) => ({
    productMap: state.productMap,
    brandMap: state.brandMap,
    lineMap: state.lineMap,
  }));

  const fieldOptions: StructuredFieldOptionMap = {
    [PRODUCT_COLUMNS.BRAND]: {
      columnKey: PRODUCT_COLUMNS.BRAND,
      columnName: 'Brand',
      isMultiValueField: true,
      filterItemOptions: {
        type: 'tree',
        filterItemFetcher: async (params) => {
          return filterMap(brandMap.values(), (brand) => {
            if (params?.searchValue) {
              if (
                !brand.name
                  .replaceAll(' ', '')
                  .toLowerCase()
                  .includes(params.searchValue.toLowerCase())
              ) {
                return null;
              }
            }
            return {
              columnKey: PRODUCT_COLUMNS.BRAND,
              filterKey: brand.id,
              filterName: brand.name,
              filterOptionNode: brand.name,
            };
          });
        },
      },
    },
    [PRODUCT_COLUMNS.LINE]: {
      columnKey: PRODUCT_COLUMNS.LINE,
      columnName: 'Product Line',
      isMultiValueField: true,
      filterItemOptions: {
        type: 'tree',
        filterItemFetcher: async (params) => {
          return filterMap(lineMap.values(), (line) => {
            if (params?.searchValue) {
              if (
                !line.name
                  .replaceAll(' ', '')
                  .toLowerCase()
                  .includes(params.searchValue.toLowerCase())
              ) {
                return null;
              }
            }
            return {
              columnKey: PRODUCT_COLUMNS.LINE,
              filterKey: line.id,
              filterName: line.name,
              filterOptionNode: line.name,
            };
          });
        },
      },
    },
    [PRODUCT_COLUMNS.PRODUCT]: {
      columnKey: PRODUCT_COLUMNS.PRODUCT,
      columnName: 'Product',
      isMultiValueField: true,
      filterItemOptions: {
        type: 'tree',
        filterItemFetcher: async (params) => {
          return createProductFilterList({
            productMap,
            brandMap,
            lineMap,
            searchValue: params?.searchValue,
          });
        },
      },
    },
    [PRODUCT_COLUMNS.PRODUCT_OPTION]: {
      columnKey: PRODUCT_COLUMNS.PRODUCT_OPTION,
      columnName: 'Product Option',
      isMultiValueField: true,
      filterItemOptions: {
        type: 'tree',
        filterItemFetcher: async (params) => {
          return createOptionFilterList(productMap, params?.searchValue);
        },
      },
    },
  };

  return fieldOptions;
};

export const createProductFilterList = ({
  productMap,
  brandMap,
  lineMap,
  searchValue,
}: {
  productMap: Map<string, ProductT>;
  brandMap: Map<string, ProductBrandOption>;
  lineMap: Map<string, ProductLineOption>;
  searchValue?: string;
}): StructuredFilterItem[] => {
  // 검색값이 있는 경우 필터링
  let filteredProducts = Array.from(productMap.values());
  if (searchValue) {
    const lowerSearchValue = searchValue.toLowerCase().replace(/\s/g, '');
    filteredProducts = filteredProducts.filter((product) => {
      return `${product.product_name}_${
        brandMap.get(product.brand_id || '')?.name
      }_${lineMap.get(product.line_id || '')?.name}`
        ?.toLowerCase()
        .replace(/\s/g, '')
        .includes(lowerSearchValue);
    });

    return filteredProducts.map((product) => {
      const brandName = brandMap.get(product.brand_id || '')?.name;
      const lineName = lineMap.get(product.line_id || '')?.name;
      return {
        columnKey: PRODUCT_COLUMNS.PRODUCT,
        filterKey: product.product_id,
        filterName: product.product_name || '',
        filterOptionNode: (
          <MainText>
            <MainText dim>
              {brandName && `${brandName} / `} {lineName && `${lineName} / `}
            </MainText>
            <MainText>{product.product_name || ''}</MainText>
          </MainText>
        ),
      };
    });
  }

  // 브랜드가 있는 제품과 없는 제품 분리
  const productsWithBrand = filteredProducts.filter(
    (product) => product.brand_id,
  );
  const productsWithoutBrand = filteredProducts.filter(
    (product) => !product.brand_id,
  );

  // 모든 제품이 브랜드가 없는 경우 브랜드로 묶지 않고 플랫 리스트 반환
  if (productsWithBrand.length === 0 && productsWithoutBrand.length > 0) {
    return productsWithoutBrand.map((product) => ({
      columnKey: PRODUCT_COLUMNS.PRODUCT,
      filterKey: product.alias,
      filterName: product.product_name || '',
      filterOptionNode: product.product_name || '',
    }));
  }

  const result: StructuredFilterItem[] = [];

  // 브랜드별로 제품 그룹화
  const productsByBrand = new Map<string, ProductT[]>();

  // 브랜드가 있는 제품들을 브랜드별로 그룹화
  productsWithBrand.forEach((product) => {
    const brandId = product.brand_id!;
    if (!productsByBrand.has(brandId)) {
      productsByBrand.set(brandId, []);
    }
    productsByBrand.get(brandId)!.push(product);
  });

  // 브랜드가 없는 제품들을 'others' 그룹에 추가
  if (productsWithoutBrand.length > 0) {
    productsByBrand.set('others', productsWithoutBrand);
  }

  // 브랜드별로 StructuredFilterItem 생성
  productsByBrand.forEach((products, brandId) => {
    let brandName: string;

    if (brandId === 'others') {
      brandName = 'Others';
    } else {
      const brand = brandMap.get(brandId);
      brandName = brand?.name || brandId;
    }

    // 브랜드 아이템 생성
    const brandItem: StructuredFilterItem = {
      columnKey: PRODUCT_COLUMNS.PRODUCT,
      filterKey: brandId,
      filterName: brandName,
      filterOptionNode: brandName,
      disableCheck: true, // 브랜드는 선택 불가능
      useDepth: true,
      childOptions: {
        fetcher: async () => {
          return products.map((product) => ({
            columnKey: PRODUCT_COLUMNS.PRODUCT,
            filterKey: product.product_id,
            filterName: product.product_name || '',
            filterOptionNode: (
              <span>
                {product.line_id && (
                  <span className="text-gray-600">
                    {lineMap.get(product.line_id)?.name} /{' '}
                  </span>
                )}
                {product.product_name}
              </span>
            ),
          }));
        },
      },
    };

    result.push(brandItem);
  });

  return result;
};

export const createOptionFilterList = (
  productMap: Map<string, ProductT>,
  searchValue?: string,
): StructuredFilterItem[] => {
  // searchValue가 있는 경우 검색 결과를 반환
  const result: StructuredFilterItem[] = [];
  productMap.values().forEach((product) => {
    if (searchValue) {
      product.options?.forEach((option) => {
        if (
          option.option_name.toLowerCase().includes(searchValue.toLowerCase())
        ) {
          result.push({
            columnKey: PRODUCT_COLUMNS.PRODUCT_OPTION,
            filterKey: option.id,
            filterName: option.option_name,
            filterOptionNode: (
              <MainText>
                <MainText dim>{product.product_name} / </MainText>
                {option.option_name}
              </MainText>
            ),
          });
        }
      });
    } else {
      result.push({
        columnKey: PRODUCT_COLUMNS.PRODUCT_OPTION,
        filterKey: product.product_id,
        filterName: product.product_name || '',
        filterOptionNode: product.product_name || '',
        disableCheck: true,
        childOptions: {
          fetcher() {
            return Promise.resolve(
              filterMap(product.options || [], (option) => {
                return {
                  columnKey: PRODUCT_COLUMNS.PRODUCT_OPTION,
                  filterKey: option.id,
                  filterName: option.option_name,
                  filterOptionNode: option.option_name,
                };
              }),
            );
          },
        },
      });
    }
  });

  return result;
};
